<template>
  <div class="iod-results">
    <h2 class="filter-header" v-if="impactAreaOptions.length > 1">Select Impact Areas</h2>
    <ChipsInput
      v-if="impactAreaOptions.length > 1"
      :items="impactAreaOptions"
      v-model="selectedImpactAreas"
      :selected-options="impactAreas"
      :show-icons="true"
      @input="(newVal) => handleImpactAreaClick(newVal)"
    />
    <CopyTrackWrapper type="impact_of_disability">
      <Transition
        :name="computedTransition"
        mode="out-in">
        <EditableWikiTextArea
          aria-live="polite"
          :class="{'empty-statement': isStatementEmptyPlaceholder}"
          v-model="statement"
          :show-buttons="!isStatementEmptyPlaceholder"
          v-if="statement.length > 0"
          @textEdited="handleTextEdit"
        >
          <template #bottom-content>
            <SaveToLibraryButton
              :user-uuid="userUuid"
              :action-permissions="actionPermissions.save"
              :content-payload="{
                statement: statement,
                disability: disabilityLabel,
                subjects: subjects,
                impactAreas: selectedImpactAreas
              }"
              :compact="true"
            />
          </template>
        </EditableWikiTextArea>
      </Transition>
    </CopyTrackWrapper>
  </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex'
import ChipsInput from '@/components/ChipsInput'
import { getIODStatementsByImpactArea, generateIODStatements } from '@/api/impact-of-disability.js'
import { buildIODStatement, buildBetaIODStatement } from '@/components/impact-of-disability/statement-builder.js'
import EditableWikiTextArea from '@/components/EditableWikiTextArea'
import CopyTrackWrapper from '@/components/CopyTrackWrapper'
import RedundantRouteMixin from '@/components/RedundantRouteMixin.js'
import SaveToLibraryButton from '@/components/goals/SaveToLibraryButton'
import { ACTION_TYPES, CONTENT_TYPES, actionPermissions } from '@/util/access_visibility'


export default {
  name: 'ImpactOfDisabilityResults',
  components: {
    ChipsInput,
    EditableWikiTextArea,
    CopyTrackWrapper,
    SaveToLibraryButton
  },
  mixins: [RedundantRouteMixin],
  props: {
    disability: {
      type: String,
      default: ''
    },
    disabilityLabel: {
      type: String,
      default: ''
    },
    subjects: {
      type: Array,
      default: () => []
    },
    impactAreas: {
      type: Array,
      default: () => []
    },
    impactAreaOptions: {
      type: Array,
      default: () => []
    },
    changeRequiresConfirmation: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedImpactAreas: this.impactAreas,
      statementOptions: {},
      statement: '',
      unEditedStatement: '',
      isStatementEmptyPlaceholder: false,
    }
  },
  computed: {
    ...mapGetters('contentProfile', ['profileId']),
    ...mapState('user', { userUuid: (state) => state.user.uuid }),
    ...mapGetters('user', ['authInfo', 'isSuperAdmin']),
    ...mapGetters('visibility', ['betaVisibility']),
    computedTransition() {
      return this.changeRequiresConfirmation ? 'none' : 'fade-in'
    },
    actionPermissions() {
      return {
        save: actionPermissions(ACTION_TYPES.SAVE, CONTENT_TYPES.IMPACT_OF_DISABILITY, {}, this.authInfo),
      }
    },
  },
  async created() {
    if (this.disability) {
      await this.pieceStatementsTogether()
    }
    this.$sendEvent('Viewed Impact of Disability Statement Results')
  },
  methods: {
    async getStatements() {
      if (this.betaVisibility) {
        if (this.subjects.length < 1 || this.disability.length < 1) {
          this.statementOptions[this.disability] = []
          return
        }
        const generatedStatementsResponse = await generateIODStatements({
          disability: this.disabilityLabel,
          subjects: this.subjects,
          impactAreas: this.impactAreas.map(ia => ia.area_label)
        })

        // Transform v2 response into the structure expected by statement-builder
        // Existing code expects: statementOptions[disability] to be an array of
        // { subject, area_uuid, statement_text } objects.
        const transformedStatements = (generatedStatementsResponse.statements || [])
          .map((stmt) => {
            const matchingImpactArea = this.impactAreaOptions.find(
              (ia) => ia.area_label === stmt.impact_area
            )

            if (!matchingImpactArea) return null

            return {
              subject: stmt.subject,
              area_uuid: matchingImpactArea.area_uuid,
              statement_text: stmt.statement_text,
            }
          })
          .filter(Boolean)

        if (!this.statementOptions[this.disability]) {
          this.statementOptions[this.disability] = []
        }

        this.statementOptions[this.disability] = transformedStatements
      } else {
        const statements = await getIODStatementsByImpactArea(this.disability, this.profileId)
        if (!this.statementOptions[this.disability]) {
          this.statementOptions[this.disability] = statements.statements;
        }
      }
    },
    async pieceStatementsTogether() {
      if (!this.statementOptions[this.disability]) {
        await this.getStatements()
      }

      if (this.betaVisibility) {
        const [betaStatement, betaIsStatementEmptyPlaceholder] = buildBetaIODStatement({
          statementsForDisability: this.statementOptions[this.disability] || [],
          selectedSubjects: this.subjects,
          selectedImpactAreas: this.impactAreas,
        })

        this.statement = betaStatement
        this.isStatementEmptyPlaceholder = betaIsStatementEmptyPlaceholder
        this.$emit('update:changeRequiresConfirmation', false)
        this.unEditedStatement = this.statement
        return
      }

      const [statement, isStatementEmptyPlaceholder] = buildIODStatement({
        impactAreaStatements: this.impactAreaOptions,
        subjectStatements: this.statementOptions,
        selectedDisability: this.disability,
        selectedSubjects: this.subjects,
        selectedImpactAreas: this.impactAreas,
      })
      this.statement = statement
      this.isStatementEmptyPlaceholder = isStatementEmptyPlaceholder
      this.$emit('update:changeRequiresConfirmation', false)
      this.unEditedStatement = this.statement
    },
    updateRoute() {
      let newQuery = Object.assign({}, this.query)
      newQuery.subjects = this.subjects
      newQuery.disability = this.disability
      newQuery.impactAreas = this.impactAreas.map(ia => ia.area_uuid)
      this.rerouteIfRouteChanged(this.$route, {
        query: newQuery,
        name: 'ImpactOfDisability'
      })
    },
    async processWatchedParam(newVal, oldVal) {
      if (oldVal !== newVal) {
        if (this.betaVisibility) {
          this.statementOptions[this.disability] = undefined
        }
        await this.pieceStatementsTogether()
        this.updateRoute()
      }
    },
    handleImpactAreaClick(newVal) {
      if (this.changeRequiresConfirmation) {
        this.$emit('confirmupdate:impactAreas', newVal)
      } else {
        this.$emit('update:impactAreas', this.selectedImpactAreas)
      }
    },
    handleTextEdit(text) {
      if (text.trim().replace(/(\s)/gm, '') !== this.unEditedStatement.trim().replace(/(\s)/gm, '')) {
        this.$emit('update:changeRequiresConfirmation', true)
        this.$sendEvent('Edited Impact of Disability Statement', {'edited_text': text})
      }
    }
  },
  watch: {
    disability: async function(newVal, oldVal) {
      await this.processWatchedParam(newVal, oldVal)
    },
    impactAreas: async function(newVal, oldVal) {
      await this.processWatchedParam(newVal, oldVal)
    },
    subjects: async function(newVal, oldVal) {
      await this.processWatchedParam(newVal, oldVal)
    },
    betaVisibility: async function(newVal, oldVal) {
      this.statementOptions[this.disability] = undefined
      await this.processWatchedParam(newVal, oldVal)
    }
  }
}
</script>
<style lang="less" scoped>
@import '~@/variables.less';
.iod-results {
  border: 1px solid @gray-light;
  border-radius: 4px;
  padding: 20px 40px 40px 40px;
}
.filter-header {
  font-size: 1rem;
  font-weight: 700;
  line-height: 20px;
  color: @gray;
  margin: 0 0 5px 0;
  padding: 5px 0;
  text-transform: uppercase;
}
/deep/ .checkboxes-container {
  border-bottom: 1px solid @gray-light;
}
/deep/ .checkbox-item {
  padding-left: 0;
  padding-bottom: 0.5rem;
  label {
    font-size: 1rem;
    &.selected {
      .chip-icon {
        font-size: 1.5rem;
      }
    }
  }
  .chip-icon {
    font-size: 1.2rem;
  }
}
/deep/ .rendered-text-container {
  font-size: 21px;
  line-height: 33px;
  padding: 2rem 1rem 0 1rem;
  p {
    margin-bottom: 1.5rem;
  }
}
/deep/ .empty-statement .rendered-text-container {
  font-style: italic;
}

/deep/ textarea {
  margin-top: 15px;
  font-weight: 400;
}
/deep/ .wiki-text-container {
  max-width: 720px;
}
.fade-in-enter-active {
  transition: opacity 300ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.fade-in-leave-active {
  transition: opacity 225ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fade-in-enter,
.fade-in-leave-to {
  opacity: 0;
}
pre {
  margin: 1rem;
}
</style>